import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  isSignal,
  output,
  signal,
  Signal,
  untracked,
} from '@angular/core';
import { LanguageService } from '@softline/application';
import {
  distinct,
  equals,
  getPropertyPath,
  getValue,
  isDefinedNotEmpty,
  isResource,
  LocalStorageDataAccess,
  Path,
  QueryBuilder,
  QueryFilter,
} from '@softline/core';
import { GridSettingsService } from '@softapps/core';
import {
  askDeleteFn,
  AsTemplateRefPipe,
  AsTypePipe,
  formatter,
  translator,
} from '@softline/ui-core';
import { AgGridAngular } from 'ag-grid-angular';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type {
  CellFocusedEvent,
  ColGroupDef,
  ColumnState,
  ExcelExportParams,
} from 'ag-grid-community';
import type {
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  DataTypeDefinition,
  DefaultMenuItem,
  FirstDataRenderedEvent,
  FullWidthCellKeyDownEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  MenuItemDef,
  RowClickedEvent,
  RowModelType,
  RowValueChangedEvent,
  SelectionChangedEvent,
  SideBarDef,
} from 'ag-grid-enterprise';
import { isQueryDataSource } from '@softapps/core';
import {
  Column,
  DetailFactory,
  Grid,
  isSortableColumn,
  isValueColumn,
  RowClickSelectEvent,
} from '../types/grid';
import { agGridAppTheme } from './ag-grid-app-theme';
import { ensureAgGridSetup } from '@softapps/core';
import { agRequestToQuery } from './ag-request-to-query.function';
import { GridLoadingOverlay } from './overlays/grid-loading.overlay';
import { GridNoRowsOverlay } from './overlays/grid-no-rows.overlay';
import { toAgGridColDefs } from './to-ag-grid-col-defs.function';

export interface ICellSelectionBounds {
  startIndex: number;
  endIndex: number;
  rowCount: number;
}

export type AddRowPosition = 'above' | 'below';

@Component({
  selector: 'soft-grid',
  imports: [AgGridAngular],
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridComponent<
  T extends object,
  TMaster extends object | undefined = undefined,
> {
  private gridApi: GridApi | null = null;
  private languageService = inject(LanguageService);
  private readonly storage = inject(LocalStorageDataAccess);
  private readonly gridSettings = inject(GridSettingsService);

  theme = agGridAppTheme;
  readonly master = input<TMaster | undefined>(undefined);

  readonly config = input.required<Grid<T>>();

  readonly serverSideFilter = input<QueryFilter<T> | null>(null);
  readonly sideBar = input<boolean | SideBarDef>(true);
  readonly enterAddsRow = input<boolean>(true);
  readonly isLoading = input<boolean>(false);
  /** Allow column sorting. Default `true`; set `false` to disable sorting on
   *  all columns (e.g. while a grid is in edit mode). */
  readonly sortable = input<boolean>(true);
  /** When set, column state (order, visibility, width, sort, pinning) is
   *  persisted to localStorage under this key and restored on load.
   *  TODO(backend): move this column-config persistence to a backend endpoint
   *  (per user/document type) once available, so it syncs across devices and is
   *  centrally managed instead of being local-only. */
  readonly stateKey = input<string | null>(null);
  f = formatter();
  t = translator();

  askDelete = askDeleteFn();

  dataTypeDefinitions: { [p: string]: DataTypeDefinition } = {
    text: {
      extendsDataType: 'text',
      baseDataType: 'text',
    },
    number: {
      extendsDataType: 'number',
      baseDataType: 'number',
    },
    boolean: {
      extendsDataType: 'boolean',
      baseDataType: 'boolean',
    },
    dateString: {
      extendsDataType: 'dateString',
      baseDataType: 'dateString',
    },
    dateTimeString: {
      extendsDataType: 'dateTimeString',
      baseDataType: 'dateTimeString',
    },
    object: {
      extendsDataType: 'object',
      baseDataType: 'object',
    },
    amount: {
      extendsDataType: 'number',
      baseDataType: 'number',
    },
    quantity: {
      extendsDataType: 'number',
      baseDataType: 'number',
    },
    percent: {
      extendsDataType: 'number',
      baseDataType: 'number',
      valueFormatter: (params) =>
        !isDefinedNotEmpty(params?.value)
          ? ''
          : '' + this.f(params.value, 'NUMBER.DECIMAL')() + '%',
    },
  };

  aggFuncs = {
    single: (params) => {
      const values: any[] = params.values;
      const distinctValues = distinct(values, { comparer: equals });
      return distinctValues.length === 1 ? distinctValues[0] : null;
    },
  };

  private readonly allowInsert = computed(() => {
    return !!this.config().insertRow;
  });

  private readonly allowEdit = computed(() => {
    return !!this.config().editRow;
  });

  private readonly allowDelete = computed(() => {
    return !!this.config().deleteRow;
  });

  readonly gridOptions: Signal<GridOptions> = computed(() => {
    const config = this.config();
    let localeText = AG_GRID_LOCALE_DE;
    switch (this.languageService.selected().code) {
      case 'de':
        localeText = AG_GRID_LOCALE_DE;
        break;
      case 'en':
        localeText = AG_GRID_LOCALE_EN;
        break;
    }
    const rowModelType: RowModelType = isQueryDataSource(config.dataSource)
      ? 'serverSide'
      : 'clientSide';

    const isServerSide = isQueryDataSource(config.dataSource);
    const sideBarInput = this.sideBar();
    const sideBar: boolean | SideBarDef =
      sideBarInput === true
        ? {
            toolPanels: ['columns', 'filters'],
            defaultToolPanel: 'columns',
          }
        : sideBarInput;
    return {
      sideBar,
      localeText,
      rowModelType,
      rowGroupPanelShow:
        config.options?.rowGroupPanelShow ?? (!config.tree ? 'onlyWhenGrouping' : 'never'),
      autoSizeStrategy: { type: 'fitCellContents' },
      getRowClass: ((fn) =>
        fn
          ? (params) => {
              const cls = params.data ? fn(params.data) : undefined;
              return cls ?? undefined;
            }
          : undefined)(config.getRowClass),
      enableCharts: true,
      cellSelection: true,
      rowNumbers: undefined,
      enableRowResizer: true,
      dataTypeDefinitions: this.dataTypeDefinitions,
      loadingOverlayComponent: GridLoadingOverlay,
      loadingOverlayComponentParams: isResource(config.dataSource)
        ? { dataSource: config.dataSource }
        : undefined,
      noRowsOverlayComponent: GridNoRowsOverlay,
      noRowsOverlayComponentParams: isResource(config.dataSource)
        ? { dataSource: config.dataSource }
        : { onReload: () => this.refresh() },
      serverSideSortAllLevels: isServerSide ? true : undefined,
      suppressServerSideFullWidthLoadingRow: isServerSide ? true : undefined,
      cacheBlockSize: isServerSide ? 100 : undefined,
      maxBlocksInCache: isServerSide ? 5 : undefined,
      rowBuffer: isServerSide ? 0 : undefined,
      maxConcurrentDatasourceRequests: isServerSide ? 1 : undefined,
      blockLoadDebounceMillis: isServerSide ? 200 : undefined,
      aggFuncs: this.aggFuncs,
      getRowId: !isServerSide
        ? (params) => {
            const data = params.data;
            if (isDefinedNotEmpty(data?.id)) return '' + data.id;
            return JSON.stringify(data);
          }
        : undefined,
      debug: isDevMode(),
      groupDisplayType: 'singleColumn',
      //suppressContextMenu: true,

      //Edit
      editType: this.allowEdit() ? 'singleCell' : undefined,
      enterNavigatesVertically: true,
      enterNavigatesVerticallyAfterEdit: true,
      undoRedoCellEditing: true,
      undoRedoCellEditingLimit: 20,
      invalidEditValueMode: 'block',
      getContextMenuItems: this.getContextMenuItems,

      //Selectable
      rowSelection: config.selectable
        ? {
            mode: config.selectable.type === 'single' ? 'singleRow' : 'multiRow',
            checkboxLocation: 'selectionColumn',
            selectionColumnDef: {
              pinned: 'left',
              width: 20,
              minWidth: 20,
              maxWidth: 20,
              resizable: false,
              suppressSizeToFit: true,
              lockPosition: 'left',
            },
            masterSelects: 'detail',
          }
        : undefined,

      //Tree
      treeData: !!config.tree,
      treeDataPath:
        config.tree && config.tree.getTreePath.type === 'path'
          ? config.tree.getTreePath.getPath
          : undefined,
      treeDataChildrenField:
        config.tree && config.tree.getTreePath.type === 'childField'
          ? config.tree.getTreePath.childrenField
          : undefined,
      treeDataParentIdField:
        config.tree && config.tree.getTreePath.type === 'parentField'
          ? config.tree.getTreePath.parentIdField
          : undefined,

      //MasterDetail
      masterDetail: !!config.detail,
      detailCellRenderer: config.detail ? DetailGridComponent : undefined,
      detailCellRendererParams: config.detail
        ? (params: ICellRendererParams) => ({ ...params, detailFactory: config.detail })
        : undefined,
      detailRowAutoHeight: config.detailOptions?.detailRowHeight === 'auto',
      detailRowHeight:
        typeof config.detailOptions?.detailRowHeight === 'number'
          ? config.detailOptions?.detailRowHeight
          : undefined,
    };
  });

  readonly columnDefs = computed<(ColDef | ColGroupDef)[]>(() => {
    const config = this.config();
    const columns = config.columns();
    const colDefs = toAgGridColDefs(
      columns,
      this.f,
      (col) => this.loadFilters(col),
      config.dataSource,
      !!config.detail,
      config.detailOptions?.masterColumn
    );
    return colDefs;
  });

  private readonly serverSideLoading = signal(false);

  readonly loading = computed(() => {
    const dataSource = this.config().dataSource;
    if (isResource(dataSource)) return dataSource.isLoading();
    if (isQueryDataSource(dataSource)) return this.serverSideLoading();
    return this.isLoading();
  });

  readonly rowData = computed(() => {
    const dataSource = this.config().dataSource;
    if (isQueryDataSource(dataSource)) return undefined;
    if (isSignal(dataSource)) return dataSource();
    if (isResource(dataSource)) return dataSource.value() ?? [];
    if (Array.isArray(dataSource)) return dataSource;
    return [];
  });

  readonly defaultColDef: Signal<ColDef> = computed(() => ({
    minWidth: 100,
    sortable: this.sortable(),
    filter: true,
    resizable: true,
    // Vertically center cell content. AG Grid centers cells in fixed-height
    // rows but top-aligns them in autoHeight rows; this keeps every column
    // centered against a row grown by a multi-line (wrapText) sibling cell.
    // The wrapText/autoHeight column overrides this with its own cellStyle.
    cellStyle: { display: 'flex', alignItems: 'center' },
  }));

  readonly autoGroupColumnDef = computed<ColDef>(() => {
    const conf = this.config();
    if (conf.tree) {
      return {
        cellRenderer: 'agGroupCellRenderer',
        width: 140,
        minWidth: 100,
        maxWidth: 200,
        suppressSizeToFit: true,
        sortable: false,
        pinned: 'left',
        lockPosition: 'left',
        headerName: conf.tree.options?.header ? conf.tree.options?.header() : undefined,
        valueGetter: conf.tree?.options?.getValue
          ? (row) => conf.tree?.options?.getValue?.(row.data)
          : undefined,
      };
    }
    return {
      minWidth: 280,
      sortable: false,
      pinned: 'left',
    };
  });

  readonly rowClicked = output<T>();
  readonly rowDoubleClicked = output<T>();
  readonly rowFocused = output<T | null>();
  readonly rowValueChanged = output<RowValueChangedEvent<T>>();
  readonly rowSelectionChanged = output<T[]>();
  readonly addRowRequested = output<void>();
  readonly rowClickSelect = output<RowClickSelectEvent<T>>();

  protected toRowClickSelectEvent(event: RowClickedEvent<T>): RowClickSelectEvent<T> {
    return {
      row: event.data as T,
      rowIndex: event.rowIndex,
      shiftKey: (event.event as MouseEvent | null)?.shiftKey ?? false,
    };
  }

  protected onRowClicked(event: RowClickedEvent<T>): void {
    // The checkbox selection column's clicks bubble up as a normal row click
    // too — don't treat toggling a checkbox as a row-click-select gesture.
    const target = event.event?.target as HTMLElement | null;
    if (target?.closest('[col-id="ag-Grid-SelectionColumn"]')) return;
    this.rowClicked.emit(event.data as T);
    this.rowClickSelect.emit(this.toRowClickSelectEvent(event));
  }

  private prevFilter: string | null = null;

  // gates the @defer around <ag-grid-angular> until the lazily loaded
  // enterprise registration (ensureAgGridSetup) has run
  protected readonly agReady = signal(false);

  constructor() {
    void ensureAgGridSetup().then(() => this.agReady.set(true));
    effect(() => {
      const filter = this.serverSideFilter();
      const filterJson = JSON.stringify(filter);
      if (filterJson === this.prevFilter) return;
      this.prevFilter = filterJson;
      const config = untracked(() => this.config());
      if (!this.gridApi || !isQueryDataSource(config.dataSource)) return;
      this.serverSideLoading.set(true);
      this.gridApi.refreshServerSide({ purge: true });
    });

    effect(() => {
      this.rowData();
      this.syncCheckboxSelection();
    });
  }

  /** Re-applies `selectable.isSelected` to the current AG Grid selection —
   *  call after an optimistic checkbox change needs to be rolled back (e.g.
   *  the persist behind it failed) to match the underlying row data again. */
  syncSelection(): void {
    this.syncCheckboxSelection();
  }

  protected syncCheckboxSelection(): void {
    const isSelected = this.config().selectable?.isSelected;
    if (!this.gridApi || !isSelected) return;
    const toSelect: IRowNode[] = [];
    const toDeselect: IRowNode[] = [];
    this.gridApi.forEachNode((node) => {
      const shouldBeSelected = node.data ? isSelected(node.data) : false;
      if (node.isSelected() === shouldBeSelected) return;
      (shouldBeSelected ? toSelect : toDeselect).push(node);
    });
    // Batch via `setNodesSelected` rather than looping `node.setSelected()` —
    // looping fires one `selectionChanged` event per node, which is a known
    // AG Grid event-storm pitfall when several rows need correcting at once.
    if (toSelect.length > 0)
      this.gridApi.setNodesSelected({ nodes: toSelect, newValue: true, source: 'api' });
    if (toDeselect.length > 0)
      this.gridApi.setNodesSelected({
        nodes: toDeselect,
        newValue: false,
        source: 'api',
      });
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    params.api.closeToolPanel();

    if (isQueryDataSource(this.config().dataSource)) {
      this.serverSideLoading.set(true);
      const datasource = this.createDataSource();
      params.api.setGridOption('serverSideDatasource', datasource);
    }

    this.moveSelectionColumnFirst();
    this.restoreColumnState();
    this.syncCheckboxSelection();
  }

  private storageKey(): string | null {
    const key = this.stateKey();
    return key ? `soft.grid.columnState.${key}` : null;
  }

  /** Whether a column layout is currently stored for this grid. */
  private hasStoredColumnState(): boolean {
    const storageKey = this.storageKey();
    if (!storageKey) return false;
    return this.storage.get<unknown[]>({ key: storageKey }) != null;
  }

  private clearStoredColumnState(): void {
    const storageKey = this.storageKey();
    if (!storageKey || !this.hasStoredColumnState()) return;
    this.storage.delete({ key: storageKey });
  }

  private restoreColumnState(): void {
    const storageKey = this.storageKey();
    if (!storageKey || !this.gridApi) return;
    // Persistence disabled globally: drop any previously-saved layout so the
    // grid falls back to its default auto-sized columns.
    if (!this.gridSettings.persistColumnState()) {
      this.clearStoredColumnState();
      return;
    }
    const state = this.storage.get<ColumnState[]>({ key: storageKey });
    if (!Array.isArray(state)) return;
    // When sorting is disabled (e.g. the editable sales-positions grid),
    // drop any persisted sort before applying — a sort saved from a
    // read-only view sharing the same stateKey would otherwise reorder the
    // rows, pushing a freshly-added empty row to the top instead of the
    // bottom.
    const sanitized: ColumnState[] = this.sortable()
      ? state
      : state.map((s) => ({ ...s, sort: null, sortIndex: null }));
    this.gridApi.applyColumnState({ state: sanitized, applyOrder: true });
  }

  persistColumnState(): void {
    const storageKey = this.storageKey();
    if (!storageKey || !this.gridApi) return;
    if (!this.gridSettings.persistColumnState()) return;
    this.storage.set({ key: storageKey }, this.gridApi.getColumnState());
  }

  private moveSelectionColumnFirst(): void {
    const api = this.gridApi;
    if (!api) return;
    const cols = api.getAllGridColumns?.() ?? [];
    const selectionCol = cols.find((c) => c.getColId() === 'ag-Grid-SelectionColumn');
    if (!selectionCol) return;
    api.setColumnsPinned?.([selectionCol], 'left');
    api.moveColumns?.([selectionCol], 0);
  }

  onFirstDataRendered(params: FirstDataRenderedEvent) {
    this.syncCheckboxSelection();
    // A restored layout already sized the columns — only auto-size when there's
    // nothing stored (or persistence is off, so nothing was restored).
    if (this.gridSettings.persistColumnState() && this.hasStoredColumnState()) {
      return;
    }
    const skipCols = ['ag-Grid-SelectionColumn', 'ag-Grid-AutoColumn'];
    const ids = (params.api.getAllGridColumns?.() ?? [])
      .map((c) => c.getColId())
      .filter((id) => !skipCols.includes(id));
    params.api.autoSizeColumns(ids);
  }

  onColumnVisible(): void {
    if (this.gridApi && isQueryDataSource(this.config().dataSource)) {
      this.serverSideLoading.set(true);
      this.gridApi.refreshServerSide({ purge: true });
    }
    this.persistColumnState();
  }

  createDataSource(): IServerSideDatasource {
    return {
      getRows: async (params: IServerSideGetRowsParams<any>) => {
        const dataSource = this.config().dataSource;
        if (!isQueryDataSource<T>(dataSource)) {
          params.fail();
          return;
        }
        const isFirstBlock = params.request.startRow === 0;
        try {
          const visibleColIds = new Set(
            params.api.getAllDisplayedColumns().map((c) => c.getColId())
          );
          const query = agRequestToQuery(
            this.config().columns(),
            params.request,
            this.serverSideFilter(),
            visibleColIds
          );
          const result = await dataSource.load({
            query,
            master: this.master() as undefined,
          });
          params.success({
            rowData: result.data,
            pivotResultFields: result.pivotFields,
          });
          if (isFirstBlock) this.serverSideLoading.set(false);
        } catch (e) {
          if (isFirstBlock) this.serverSideLoading.set(false);
          params.fail();
        }
      },
    };
  }

  async loadFilters(col: Column<T>): Promise<unknown[]> {
    const dataSource = this.config().dataSource;
    const externalFilter = this.serverSideFilter();
    if (!isQueryDataSource(dataSource) || !isValueColumn(col)) return [];

    const field = getPropertyPath(col.getValue);
    let sortName: Path<T> | undefined;
    if (isSortableColumn(col)) {
      const sortField = getPropertyPath(col.sortBy);
      sortName = isDefinedNotEmpty(sortField)
        ? (`${field}.${sortField}` as Path<T>)
        : (field ?? undefined);
    }
    if (!field) return [];
    const builder = new QueryBuilder<T>()
      .start(0)
      .end(1000)
      .column(field, { usage: 'group' })
      .sort(sortName ?? field, 'ASC');

    if (isDefinedNotEmpty(externalFilter)) builder.filter(externalFilter);

    const result = await dataSource.load({ query: builder.build() });
    return result.data.map((o) => getValue(o, field));
  }

  async onRowValueChanged(event: RowValueChangedEvent<T>): Promise<void> {
    const mutations = this.config().editRow?.mutations;
    if (mutations?.update && event.data) {
      await mutations.update(event.data, { entity: event.data });
    }
    this.rowValueChanged.emit(event);
  }

  async onCellValueChanged(event: CellValueChangedEvent<T>): Promise<void> {
    if (event.oldValue === event.newValue) return;
    const mutations = this.config().editRow?.mutations;
    if (!mutations?.update || !event.data) return;
    await mutations.update(event.data, { entity: event.data });
  }

  refresh(): boolean {
    if (isQueryDataSource(this.config().dataSource)) {
      this.serverSideLoading.set(true);
      this.gridApi?.refreshServerSide({ purge: true });
    } else this.gridApi?.refreshClientSideRowModel();
    return true;
  }

  private getCellSelectionBounds(
    params: GetContextMenuItemsParams
  ): ICellSelectionBounds {
    const cellRanges = this.gridApi?.getCellRanges();
    if (!cellRanges || !cellRanges[0]?.startRow || !cellRanges[0]?.endRow) {
      const rowIndex = params.node?.rowIndex || 0;
      return { startIndex: rowIndex, endIndex: rowIndex, rowCount: 1 };
    }

    const cellRangeStartRowIndex = cellRanges[0].startRow.rowIndex;
    const cellRangeEndRowIndex = cellRanges[0].endRow.rowIndex;

    const rowCount = Math.abs(cellRangeEndRowIndex - cellRangeStartRowIndex) + 1;

    const startIndex = Math.min(cellRangeStartRowIndex, cellRangeEndRowIndex);
    const endIndex = Math.max(cellRangeStartRowIndex, cellRangeEndRowIndex);

    return { startIndex, endIndex, rowCount };
  }

  private startEditingCell(insertIndex: number, firstColumn: string) {
    if (!this.gridApi) return;
    this.gridApi.clearCellSelection();
    this.gridApi.setFocusedCell(insertIndex, firstColumn);
    this.gridApi.startEditingCell({
      rowIndex: insertIndex,
      colKey: firstColumn,
    });
  }

  private addRows(rowCount: number, startIndex?: number) {
    if (!this.gridApi) return;
    const newRows = Array.from({ length: rowCount }, () => ({
      // Default value of the new Row
    }));

    const insertIndex = startIndex ?? 0;

    const result = this.gridApi.applyTransaction({
      add: newRows,
      addIndex: insertIndex,
    });

    if (result && result?.add?.length > 0) {
      requestAnimationFrame(() => {
        this.startEditingCell(insertIndex, this.columnDefs[0].field || '');
      });
    }
  }

  private async deleteRows(startIndex: number, endIndex: number): Promise<void> {
    const deleteCallback = this.config().deleteRow?.mutations?.delete;
    if (!this.gridApi || !deleteCallback) return;
    const rowDataToRemove: unknown[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const node = this.gridApi.getDisplayedRowAtIndex(i);
      if (node?.data) {
        rowDataToRemove.push(node.data);
      }
    }

    if (rowDataToRemove.length === 0) return;
    if ((await this.askDelete({ question: '' })) !== 'YES') return;

    for (const row of rowDataToRemove) {
      await deleteCallback({ entity: row as T });
      this.gridApi.applyTransaction({ remove: [row] });
    }

    this.gridApi.clearCellSelection();
  }

  protected getContextMenuItems = (
    params: GetContextMenuItemsParams
  ): (DefaultMenuItem | MenuItemDef)[] => {
    const { startIndex, endIndex, rowCount } = this.getCellSelectionBounds(params);
    const rowLabel = `${rowCount} Row${rowCount !== 1 ? 's' : ''}`;
    const returnValue: (DefaultMenuItem | MenuItemDef)[] = [];
    if (this.allowInsert()) {
      returnValue.push(
        {
          name: `Insert Row Above`,
          action: () => this.addRows(1, startIndex),
          icon: '<span class="ag-icon ag-icon-plus"></span>',
        },
        {
          name: `Insert Row Below`,
          action: () => this.addRows(1, endIndex + 1),
          icon: '<span class="ag-icon ag-icon-plus"></span>',
        },
        'separator'
      );
    }
    if (this.allowDelete()) {
      returnValue.push(
        {
          name: `Delete ${rowLabel}`,
          action: () => this.deleteRows(startIndex, endIndex),
          icon: '<span class="ag-icon ag-icon-minus"></span>',
        },
        'separator'
      );
    }
    const rowActions = this.config().rowActions;
    const row = params.node?.data as T | undefined;
    if (rowActions?.actions.length && row) {
      const visible = rowActions.actions.filter(
        (rowAction) => !rowAction.visible || rowAction.visible(row)
      );
      for (const rowAction of visible) {
        returnValue.push({
          name: rowAction.label,
          icon: rowAction.icon ? `<i class="${rowAction.icon}"></i>` : undefined,
          disabled: rowAction.enabled ? !rowAction.enabled(row) : false,
          action: () => rowAction.action(row),
        });
      }
      if (visible.length && !rowActions.suppressDefaults) returnValue.push('separator');
    }
    if (rowActions?.suppressDefaults) return returnValue;

    return [...returnValue, ...(params.defaultItems ?? [])];
  };

  protected async onCellKeyDown(
    event: CellKeyDownEvent | FullWidthCellKeyDownEvent<any, any>
  ): Promise<void> {
    const { api, node, event: keyboardEvent } = event;
    const key = (keyboardEvent as KeyboardEvent)?.key;
    const lastRowIndex = api.getDisplayedRowCount() - 1;

    if (!this.allowInsert()) return;
    if (node.rowIndex !== lastRowIndex) return;

    if (key === 'Enter') {
      if (this.enterAddsRow()) {
        this.addRows(1, lastRowIndex + 1);
      } else {
        this.addRowRequested.emit();
      }
    } else if (key === 'ArrowDown') {
      (keyboardEvent as KeyboardEvent)?.preventDefault();
      this.pendingFocusColId =
        'column' in event ? (event.column?.getColId() ?? null) : null;
      this.addRowRequested.emit();
    }
  }

  private pendingFocusColId: string | null = null;

  onSelectionChanged(event: SelectionChangedEvent<any, any>): void {
    // Programmatic sync from `selectable.isSelected` (see syncCheckboxSelection) —
    // not a user action, don't round-trip it back to the consumer.
    if (event.source === 'api') return;
    this.rowSelectionChanged.emit(event.api.getSelectedRows());
  }

  onCellFocused(event: CellFocusedEvent): void {
    if (event.rowIndex == null || !event.api) {
      this.rowFocused.emit(null);
      return;
    }
    const node = event.api.getDisplayedRowAtIndex(event.rowIndex);
    this.rowFocused.emit((node?.data as T) ?? null);
  }

  gotoRow(index: number): void {
    if (!this.gridApi) return;
    const firstCol = this.gridApi.getAllDisplayedColumns()[0];
    const allColumns = this.gridApi.getAllDisplayedColumns();
    const lastRowIndex = this.gridApi.getDisplayedRowCount() - 1;
    this.gridApi.ensureIndexVisible(index);
    this.gridApi.clearCellSelection();
    this.gridApi.addCellRange({
      rowStartIndex: index,
      rowEndIndex: index,
      columns: allColumns,
    });
  }

  deselectAll(): void {
    if (!this.gridApi) return;
    this.gridApi.deselectAll();
    this.gridApi.clearCellSelection();
  }

  exportExcel(params?: ExcelExportParams): void {
    this.gridApi?.exportDataAsExcel(params);
  }

  focusLastRow(colId?: string): void {
    if (!this.gridApi) return;
    const lastRowIndex = this.gridApi.getDisplayedRowCount() - 1;
    if (lastRowIndex < 0) return;
    const targetCol =
      (colId && this.gridApi.getColumn(colId)) ||
      (this.pendingFocusColId && this.gridApi.getColumn(this.pendingFocusColId)) ||
      this.gridApi.getAllDisplayedColumns()[0];
    const targetColId = targetCol ? targetCol.getColId() : null;
    this.pendingFocusColId = null;
    if (!targetCol) return;
    this.gridApi.ensureIndexVisible(lastRowIndex);
    this.gridApi.clearCellSelection();
    this.gridApi.setFocusedCell(lastRowIndex, targetCol);
    this.gridApi.addCellRange({
      rowStartIndex: lastRowIndex,
      rowEndIndex: lastRowIndex,
      columns: [targetCol],
    });
    requestAnimationFrame(() => {
      if (!targetColId) return;
      const cell = document.querySelector(
        `.ag-row[row-index="${lastRowIndex}"] .ag-cell[col-id="${targetColId}"] input`
      ) as HTMLInputElement | null;
      cell?.focus();
    });
  }

  refreshRows(rowIds?: ReadonlySet<string> | string[]): void {
    if (!this.gridApi) return;
    if (!rowIds) {
      this.gridApi.redrawRows();
      return;
    }
    const ids = rowIds instanceof Set ? rowIds : new Set(rowIds);
    if (ids.size === 0) return;
    const nodes = [] as { id?: string }[];
    this.gridApi.forEachNode((node) => {
      const nodeId = node.data?.id;
      if (nodeId != null && ids.has(String(nodeId))) nodes.push(node);
    });
    if (nodes.length === 0) return;
    this.gridApi.redrawRows({ rowNodes: nodes as never });
  }
}

@Component({
  standalone: true,
  imports: [
    GridComponent,
    NgComponentOutlet,
    AsTemplateRefPipe,
    AsTypePipe,
    NgTemplateOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (detail(); as detail) {
      @if (detail | asTemplateRef; as tplRef) {
        <ng-container *ngTemplateOutlet="tplRef; context: { $implicit: master() }" />
      } @else if (detail | asType; as type) {
        <ng-container *ngComponentOutlet="type; inputs: { master: master() }" />
      } @else {
        <soft-grid class="p-8" [config]="$any(detail)" [master]="master()" #grid />
      }
    }
  `,
})
class DetailGridComponent<
  T extends object,
  TMaster extends object,
> implements ICellRendererAngularComp {
  params!: ICellRendererParams;
  masterGridApi!: GridApi;
  rowId!: string;

  readonly master = signal<TMaster | undefined>(undefined);
  readonly detailFactory = signal<DetailFactory<object, T> | null>(null);
  readonly detail = computed(() => {
    const factory = this.detailFactory();
    if (!factory) return null;
    return factory(this.params.data);
  });

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.master.set(params.data);
    this.detailFactory.set(
      (params as unknown as { detailFactory: DetailFactory<object, T> }).detailFactory
    );

    this.masterGridApi = params.api;
    this.rowId = params.node.id as string;
  }

  refresh(params: ICellRendererParams<any, any, any>): boolean {
    this.master.set(params.data);
    return false;
  }
}
